/**
 * Services execution requests parked in `aiExecutionStore` for one query tab.
 *
 * Two callers share this seam:
 * - **MCP `ui_execute_query`** — fire-and-forget. The request resolves the
 *   moment execution starts, which is what that tool has always promised.
 * - **The agent execute-with-confirm protocol** — sets `awaitCompletion`, so
 *   the request resolves only once the run really finished. That outcome is
 *   what gets written back to the agent, so it has to be truthful.
 *
 * `awaitCompletion` is also what makes the re-entry guard necessary: the
 * request stays in the store for the whole run, while ordinary re-renders
 * (a keystroke debounce flush, a store update) re-enter this effect against
 * the same pending request. Without `startedRequestIdRef` the query runs
 * twice. Options are read through a ref so the effect's dependencies stay
 * limited to the request itself.
 */

import { useCallback, useEffect, useRef } from 'react';
import {
  useAiExecutionStore,
  AI_EXECUTION_SQL_CHANGED,
  AI_EXECUTION_TARGET_CHANGED,
} from '../../../terminal/store/aiExecutionStore';
import type { QueryExecutionOutcome } from './useQueryExecution';

/**
 * Two differences are artefacts of the editor rather than changes to what the
 * user read, and only these two are forgiven:
 *
 * - **Line endings.** Monaco normalises a model's EOLs on `setValue`, so SQL
 *   captured from the workspace store can be CRLF while the buffer reports LF
 *   for the exact same characters.
 * - **A byte-order mark.** An agent-written `.sql` file carrying a UTF-8 BOM
 *   leaves the store BOM-prefixed while `getValue()` drops it. Left in, that
 *   tab would refuse every request forever — a dead end, not a safety win.
 *
 * Everything else must match exactly.
 */
function normalizeForComparison(value: string): string {
  return value.replace(/\r\n?/g, '\n').replace(/\uFEFF/g, '');
}

export type AiExecutionRunnerOptions = {
  tabId: string;
  connectionId?: string;
  /** The tab's live database, read at execution time for the target binding. */
  database?: string;
  /** Resolve the exact text that will run (selection or full buffer). */
  getTextToExecute: (selectedOnly: boolean) => string;
  /** Start execution without waiting for it — the historical MCP behaviour. */
  startExecution: (textToExecute: string) => void;
  /**
   * Run through the dangerous-query gate and settle with the real outcome.
   *
   * `guard` must be re-run immediately before the query is actually sent — the
   * confirmation dialog can sit open for minutes, and a non-null return means
   * the tab was rebound in the meantime and nothing may run.
   */
  runToCompletion: (
    textToExecute: string,
    guard?: () => string | null,
  ) => Promise<QueryExecutionOutcome>;
};

export function useAiExecutionRunner(options: AiExecutionRunnerOptions): void {
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const pendingRequest = useAiExecutionStore(
    useCallback((s) => s.pendingRequests.get(options.tabId)?.request, [options.tabId])
  );
  const resolveRequest = useAiExecutionStore((s) => s.resolveRequest);

  // Which request has already been started. Never cleared: request ids are
  // unique, and a stale entry can only ever suppress a duplicate run.
  const startedRequestIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!pendingRequest) return;
    if (startedRequestIdRef.current === pendingRequest.requestId) return;
    startedRequestIdRef.current = pendingRequest.requestId;

    // Read through the ref so these are the tab's values NOW, not the ones
    // captured when the effect's dependencies last changed.
    // (`database` is deliberately not destructured — the target check below
    // re-reads it from the ref every time it runs, including from inside the
    // guard long after this effect body has returned.)
    const { tabId, connectionId, getTextToExecute, startExecution, runToCompletion } =
      optionsRef.current;

    if (!connectionId) {
      resolveRequest(tabId, { success: false, error: 'No connection' });
      return;
    }

    // Consent binding, target side: an approved run must land where the user
    // was told it would. A rebind between approval and execution refuses,
    // exactly as a changed statement does.
    //
    // Checked twice on purpose. Once here, and again through `targetGuard`
    // just before the query is sent — because the destructive-query dialog
    // sits between the two for as long as the user takes to read it.
    const expectedTarget = pendingRequest.expectedTarget;
    const targetMatches = (): boolean => {
      if (expectedTarget === undefined) return true;
      const live = optionsRef.current;
      return (
        expectedTarget.connectionId === live.connectionId &&
        expectedTarget.database === live.database
      );
    };
    const targetGuard = (): string | null => (targetMatches() ? null : AI_EXECUTION_TARGET_CHANGED);

    if (!targetMatches()) {
      resolveRequest(tabId, { success: false, error: AI_EXECUTION_TARGET_CHANGED });
      return;
    }

    const textToExecute = getTextToExecute(pendingRequest.selectedOnly === true);

    // Consent binding: what runs must be what the user was shown, or nothing
    // runs. The approved text is deliberately NOT executed as a fallback —
    // running SQL the editor no longer displays would be its own deception.
    if (
      pendingRequest.expectedSql !== undefined &&
      normalizeForComparison(textToExecute) !== normalizeForComparison(pendingRequest.expectedSql)
    ) {
      resolveRequest(tabId, { success: false, error: AI_EXECUTION_SQL_CHANGED });
      return;
    }

    if (!textToExecute.trim()) {
      resolveRequest(tabId, { success: false, error: 'No query to execute' });
      return;
    }

    const executionId = `ui-exec-${Date.now()}`;

    if (!pendingRequest.awaitCompletion) {
      startExecution(textToExecute);
      resolveRequest(tabId, { success: true, executionId });
      return;
    }

    void runToCompletion(textToExecute, targetGuard).then((result) => {
      resolveRequest(
        tabId,
        result.success
          ? { success: true, executionId }
          : { success: false, error: result.errorMessage ?? 'Query execution failed.' }
      );
    });
  }, [pendingRequest, resolveRequest]);
}
